import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Différence entre trésorerie et résultat : ce que tout dirigeant doit savoir | Trezoone',
    description: 'Comprendre la différence entre trésorerie et résultat comptable est crucial. Un résultat positif ne garantit pas une trésorerie positive. Explication claire.',
    canonical: '/difference-tresorerie-resultat',
    keywords: 'différence trésorerie résultat, trésorerie vs résultat, trésorerie et comptabilité, résultat comptable trésorerie',
  },
  hero: {
    tag: 'Trésorerie vs résultat',
    title: 'Mon expert-comptable me dit que je suis bénéficiaire. Alors pourquoi mon compte est vide ?',
    subtitle: 'C\'est LA question que posent tous les dirigeants un jour. La réponse est simple — et essentielle à comprendre pour piloter votre entreprise.',
    ctaLabel: 'Comprendre ma situation',
    ctaHref: '/simulateur-tresorerie-association',
  },
  pilierLink: { label: 'Guide gestion trésorerie', href: '/gestion-tresorerie' },
  sections: [
    {
      type: 'explain',
      title: 'La différence fondamentale entre trésorerie et résultat',
      content: 'Deux logiques complètement différentes :',
      items: [
        'RÉSULTAT = vision comptable → enregistre les charges et produits quand ils sont engagés (même si le cash n\'a pas encore bougé)',
        'TRÉSORERIE = vision cash → mesure les flux réels d\'argent sur votre compte bancaire',
        'Exemple : vous facturez 10 000 € en octobre → résultat +10 000 € en octobre, mais trésorerie +10 000 € en décembre si le client paie à 60 jours',
        'Le décalage entre les deux, c\'est votre besoin de financement à court terme',
      ],
    },
    {
      type: 'example',
      title: 'Cas réel : entreprise bénéficiaire en rupture de trésorerie',
      content: 'Une agence de communication, résultat annuel prévisionnel +25 000 €. Et pourtant…',
      items: [
        'Résultat comptable : +25 000 € → bénéficiaire, tout semble bien',
        'Mais : 3 gros contrats signés en novembre → encaissements en janvier-février',
        'Charges de novembre/décembre : 35 000 € → à payer maintenant',
        'Trésorerie décembre : -10 000 € malgré un exercice bénéficiaire',
        'Solution : anticiper ce décalage avec un plan de trésorerie',
      ],
    },
    {
      type: 'midcta',
      title: 'Bénéficiaire sur le papier, à découvert en décembre — comprenez pourquoi et évitez la prochaine fois',
      content: 'Trezoone projette votre solde bancaire réel sur 12 mois et identifie les décalages entre facturation et encaissement avant qu\'ils créent une tension.',
    },
    {
      type: 'solution',
      title: 'Trezoone : la vision trésorerie que n\'a pas votre comptable',
      content: 'Votre expert-comptable optimise votre résultat. Trezoone surveille votre cash. Les deux sont nécessaires.',
      items: [
        'Vision cash en temps réel, pas en différé comme en comptabilité',
        'Projection du solde bancaire réel sur 12 mois',
        'Identification des décalages entre facturation et encaissement',
        'Alerte quand le décalage crée une tension de trésorerie',
        'Rapport lisible pour votre CA et vos financeurs',
      ],
    },
    {
      type: 'cluster',
      title: 'Comprendre les fondamentaux de la trésorerie d\'entreprise',
      links: [
        { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
        { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
        { label: 'BFR et trésorerie', href: '/bfr-tresorerie' },
        { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
        { label: 'Suivi trésorerie entreprise', href: '/suivi-tresorerie-entreprise' },
        { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
      ],
    },
    {
      type: 'cta',
      title: 'Comprendre sa trésorerie, c\'est survivre et grandir',
      content: 'Testez Trezoone, l\'outil qui vous donne la vision cash que votre comptable ne peut pas vous donner. Gratuit 14 jours.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
    { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
    { label: 'BFR et trésorerie', href: '/bfr-tresorerie' },
    { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
    { label: 'Suivi trésorerie entreprise', href: '/suivi-tresorerie-entreprise' },
    { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function TresorerieVsResultatPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
